/**
 * API Router Service
 * Routes API calls through WebRTC when in cloud mode, direct HTTP otherwise
 */

import { getRuntimeConfig, isElectron, isElectronLocalApiUrl } from '../config/runtime';
import type { ApiTransportOptions } from './apiTransport';
import { markHubResponse } from '../utils/bootTiming';
import type { ElectronHttpAnswer } from './hubFetch';
import {
  CHANNEL_WAIT_POLL_MS,
  HubChannelUnavailableError,
  decideTransport,
  type TransportPlan,
} from './hubRequestRouting';
import { useWebRTCStore } from '../stores/webrtc';
import { omitKeys } from '../utils/omitKeys';
import { LOCAL_REQUEST_BUDGET_HEADER } from '../../electron/localProtocolLadder';

// 64KB chunks for large payloads
const UPLOAD_CHUNK_SIZE = 240 * 1024; // raw bytes per binary envelope (~256KB SCTP max minus header)
// Large file uploads (IFC models, media) can take minutes over a WebRTC data
// channel; the default 30s request budget is far too short for init/complete.
const UPLOAD_REQUEST_TIMEOUT_MS = 5 * 60 * 1000;
const DEBUG_API_ROUTER = import.meta.env.VITE_DEBUG_API_ROUTER === 'true';

function debugApiRouter(...args: unknown[]): void {
  if (DEBUG_API_ROUTER) {
    console.log(...args);
  }
}

interface RoutedRequestInit extends RequestInit, ApiTransportOptions {
  // Additional options for routing
  onUploadProgress?: (percent: number) => void;
}

interface RoutedWebRTCResponse {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: string;
  /** Raw binary payload for large downloads (set directly on the SCTP download channel). */
  bodyBinary?: ArrayBuffer;
}

interface MultipartField {
  name: string;
  value: string;
}

interface MultipartFileDescriptor {
  uploadId: string;
  fieldName: string;
  fileName: string;
  mimeType: string;
  size: number;
}

interface MultipartFileUpload extends MultipartFileDescriptor {
  file: File;
}

function appendQueryParam(path: string, key: string, value: string): string {
  const url = new URL(path, window.location.origin);
  url.searchParams.set(key, value);
  return `${url.pathname}${url.search}`;
}

function buildElectronResponse(result: ElectronHttpAnswer): Response {
  let body: BodyInit | null = null;

  if (result.bodyEncoding === 'base64' && result.body) {
    const binary = atob(result.body);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    body = bytes;
  } else if (typeof result.body === 'string') {
    body = result.body;
  }

  return new Response(body, {
    status: result.status,
    statusText: result.statusText,
    headers: new Headers(result.headers || {}),
  });
}

async function electronSafeFetch(url: string, options: RoutedRequestInit = {}): Promise<Response> {
  if (isElectron() && isElectronLocalApiUrl(url)) {
    if (options.body instanceof FormData) {
      return fetch(url, options);
    }
    const electron = window.electron;
    if (electron?.httpRequest) {
      const headers = { ...((options.headers as Record<string, string>) || {}) };
      // The main process owns the local proxy's budget (30 s by default); this
      // is how a request that legitimately takes longer asks for more.
      if (options.timeoutMs) {
        headers[LOCAL_REQUEST_BUDGET_HEADER] = String(options.timeoutMs);
      }
      const rawBody =
        typeof options.body === 'string' ? options.body : options.body ? JSON.stringify(options.body) : undefined;

      const result = await electron.httpRequest({
        url,
        method: options.method || 'GET',
        headers,
        body: rawBody,
      });

      markHubResponse(url, result.status, 'ipc');

      if (result.status === 0) {
        throw new Error(result.statusText || 'Electron request failed');
      }

      return buildElectronResponse(result);
    }
  }

  return fetch(url, options);
}

/**
 * The active hub's connection key — the scope every hub-level cache uses.
 *
 * Public `routedFetch` already resolves the same two facts (the `/h/:hubId` route
 * wins, then the stored cloud key); a hub-level cache must scope by the same
 * answer or a model loaded for one hub could be served to another.
 */
export function activeHubKey(): string {
  return currentRouteHubId() ?? activeCloudHubId() ?? 'local';
}

/** Extract the active hub id from the current route (/h/:hubId/…, hash or path). */
function currentRouteHubId(): string | null {
  try {
    const hashMatch = window.location.hash.match(/[#/]h\/([0-9a-fA-F-]{36})/);
    if (hashMatch) return hashMatch[1];
    const pathMatch = window.location.pathname.match(/\/h\/([0-9a-fA-F-]{36})/);
    return pathMatch ? pathMatch[1] : null;
  } catch {
    return null;
  }
}

/**
 * The hub id the session belongs to before the /h/:hubId route has landed
 * (written by connectCloudHub / setActiveHub). Only a `hub:` key counts — a
 * `local:` key is a LAN connection, not a WebRTC one.
 */
function activeCloudHubId(): string | null {
  try {
    const stored = localStorage.getItem('otto_active_hub_key') || '';
    return stored.startsWith('hub:') && stored !== 'hub:undefined' ? stored.slice('hub:'.length) : null;
  } catch {
    return null;
  }
}

/**
 * Hold a hub request until the channel is up. A page boot (or a reconnect after
 * a dropped session) fires requests before the data channel is ready; answering
 * them at the cloud origin instead is what produced the cloud nginx 404s.
 */
async function waitForHubChannel(webrtcStore: ReturnType<typeof useWebRTCStore>, timeoutMs: number): Promise<void> {
  const deadline = Date.now() + timeoutMs;
  while (!webrtcStore.connected && Date.now() < deadline) {
    await new Promise((resolve) => {
      setTimeout(resolve, CHANNEL_WAIT_POLL_MS);
    });
  }
}

/**
 * Main routing function - replaces fetch() for API calls
 */
export async function routedFetch(url: string, options: RoutedRequestInit = {}): Promise<Response> {
  const config = getRuntimeConfig();
  const webrtcStore = useWebRTCStore();

  const routeHubId = currentRouteHubId();
  const plan: TransportPlan = decideTransport({
    mode: config.mode,
    channel: {
      connected: webrtcStore.connected,
      connecting: webrtcStore.connecting,
      hubId: webrtcStore.hubId,
    },
    routeHubId,
    activeCloudHubId: activeCloudHubId(),
    url,
    appOrigin: window.location.origin,
    apiUrls: [config.apiUrl, config.cloudApiUrl],
    hubServed: config.hubServed === true,
  });

  console.log(
    `[ApiRouter] ${options.method || 'GET'} ${url} | mode=${config.mode} webrtc=${plan.viaChannel} connected=${webrtcStore.connected} hub=${webrtcStore.hubId} route=${routeHubId}`,
  );

  if (!plan.viaChannel) {
    return electronSafeFetch(url, options);
  }

  // Cloud mode - the channel carries every hub call. A request that plain HTTP
  // could only answer at the cloud origin waits for a channel that is expected
  // to come up (page boot, hub switch, reconnect) instead of 404ing there.
  if (!plan.useChannelNow && plan.waitForChannelMs > 0) {
    await waitForHubChannel(webrtcStore, plan.waitForChannelMs);
  }

  if (!webrtcStore.connected) {
    if (!plan.mayFallBackToHttp) {
      console.error(`[ApiRouter] No hub channel for ${url} — NOT sending a hub path to the cloud origin`);
      throw new HubChannelUnavailableError(url, plan.waitForChannelMs);
    }
    console.warn(`[ApiRouter] WebRTC not connected, falling back to HTTP`);
    return electronSafeFetch(url, options);
  }

  try {
    const response = await webrtcFetch(url, options);
    markHubResponse(url, response.status, 'channel');
    return response;
  } catch (error) {
    // In Electron/cloud mode, falling back to otto-local://localhost routes the
    // request to the user's own machine instead of the remote hub. That can turn
    // a transient WebRTC failure into a misleading local 404 (e.g. Room.vue then
    // redirects away). Surface the WebRTC error instead of silently misrouting it.
    if (
      webrtcStore.connected &&
      (config.mode === 'electron' || config.mode === 'cloud') &&
      isElectronLocalApiUrl(url)
    ) {
      console.error(`[ApiRouter] WebRTC request failed; suppressing local HTTP fallback for ${url}:`, error);
      throw error;
    }

    // The same rule for the cloud origin: a hub path cannot be answered there, so
    // the HTTP "fallback" would only replace a channel error with a 404 from the
    // wrong server.
    if (!plan.mayFallBackToHttp) {
      console.error(
        `[ApiRouter] WebRTC request failed and the cloud origin cannot answer ${url}; surfacing the channel error:`,
        error,
      );
      throw error;
    }

    console.error(`[ApiRouter] WebRTC request failed, falling back to HTTP:`, error);
    return electronSafeFetch(url, options);
  }
}

/**
 * Fetch implementation using WebRTC data channel
 */
async function webrtcFetch(url: string, options: RoutedRequestInit = {}): Promise<Response> {
  const webrtcStore = useWebRTCStore();

  // Parse URL to get path (remove base URL if present)
  const urlObj = new URL(url, window.location.origin);
  const path = urlObj.pathname + urlObj.search;

  // Prepare headers
  const headers: Record<string, string> = {};
  if (options.headers) {
    if (options.headers instanceof Headers) {
      options.headers.forEach((value, key) => {
        headers[key] = value;
      });
    } else if (Array.isArray(options.headers)) {
      options.headers.forEach(([key, value]) => {
        headers[key] = value;
      });
    } else {
      Object.assign(headers, options.headers);
    }
  }

  // Handle body
  const body = options.body;

  if (body instanceof FormData) {
    const uploadResponse = await uploadFormDataViaWebRTC(path, body, {
      method: options.method || 'POST',
      headers,
      onProgress: options.onUploadProgress,
    });

    return new Response(uploadResponse.body, {
      status: uploadResponse.status,
      statusText: uploadResponse.statusText,
      headers: new Headers(uploadResponse.headers),
    });
  }

  // Send request through WebRTC.
  //
  // Let the failure reach routedFetch, which owns the transport policy: a hub
  // path the cloud origin cannot answer must NOT be retried over plain HTTP
  // (that 404s against the cloud app), while a direct hub URL still may be.
  // Binary downloads (X-Download-Id envelope transfers) can intermittently
  // lose chunks when several large downloads run concurrently on the SCTP
  // channel, leaving the client's idle-stall timer to reject with
  // "Binary download stalled". Retry once before surfacing the error — a
  // fresh request usually succeeds.
  let response: RoutedWebRTCResponse;
  for (let attempt = 0; ; attempt += 1) {
    try {
      response = await webrtcStore.sendRequest({
        method: options.method || 'GET',
        path,
        headers,
        requestClass: options.requestClass,
        timeoutMs: options.timeoutMs,
        body: body ? (typeof body === 'string' ? body : JSON.stringify(body)) : undefined,
      });
      break;
    } catch (__caught: unknown) {
      const err = __caught as Error;
      const message = err?.message ? String(err.message) : String(err);
      const retryable =
        message.includes('Binary download stalled') || message.includes('Request channel not available');
      if (attempt >= 1 || !retryable) {
        throw err;
      }
      console.warn(`[ApiRouter] WebRTC ${message}, retrying ${path} (attempt ${attempt + 1})`);
      // Give the channel a beat to flush before retrying.
      await new Promise((resolve) => {
        setTimeout(resolve, 500);
      });
    }
  }

  console.log(
    `[ApiRouter] WebRTC response: ${response!.status} ${path} | bodyLen=${typeof response!.body === 'string' ? response!.body.length : 'N/A'} | ctype=${response!.headers?.['content-type'] || 'none'}`,
  );

  // Large binary responses come via the download channel as raw binary.
  // bodyBinary is set directly — no base64 decode needed.
  if (response!.bodyBinary) {
    return new Response(response!.bodyBinary, {
      status: response!.status,
      statusText: response!.statusText,
      headers: new Headers(response!.headers),
    });
  }

  // The hub base64-encodes binary payloads (images, snapshots, etc.) because the
  // WebRTC data channel only carries JSON strings.  Detect this by checking the
  // content-type: anything that is not JSON or plain text was encoded as base64
  // and must be decoded back to binary before constructing the synthetic Response,
  // otherwise calling .blob() on it yields corrupted data (raw base64 ASCII bytes).
  const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
  const isTextual = contentType.includes('application/json') || contentType.startsWith('text/');

  let responseBody: BodyInit = response.body;
  if (!isTextual && response.body) {
    try {
      const binaryStr = atob(response.body);
      const bytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) {
        bytes[i] = binaryStr.charCodeAt(i);
      }
      responseBody = bytes.buffer;
    } catch {
      // atob failed — body was not base64; pass through as-is.
    }
  }

  // Convert WebRTC response to Response object
  return new Response(responseBody, {
    status: response.status,
    statusText: response.statusText,
    headers: new Headers(response.headers),
  });
}

async function uploadFormDataViaWebRTC(
  path: string,
  formData: FormData,
  options: {
    method?: string;
    headers?: Record<string, string>;
    onProgress?: (percent: number) => void;
  } = {},
): Promise<RoutedWebRTCResponse> {
  const webrtcStore = useWebRTCStore();

  if (!webrtcStore.connected) {
    throw new Error('WebRTC not connected');
  }

  const fields: MultipartField[] = [];
  const files: MultipartFileUpload[] = [];

  formData.forEach((value, key) => {
    if (value instanceof File) {
      files.push({
        uploadId: `upload_${Date.now()}_${files.length}_${Math.random().toString(36).slice(2)}`,
        fieldName: key,
        fileName: value.name || `${key}.bin`,
        mimeType: value.type || 'application/octet-stream',
        size: value.size,
        file: value,
      });
      return;
    }

    fields.push({ name: key, value: String(value) });
  });

  if (files.length === 0) {
    throw new Error('FormData WebRTC upload requires at least one file');
  }

  const method = (options.method || 'POST').toUpperCase();
  const headers = { ...(options.headers || {}) };
  delete headers['Content-Type'];
  delete headers['content-type'];

  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  let uploadedBytes = 0;

  for (const file of files) {
    const totalChunks = Math.max(1, Math.ceil(file.size / UPLOAD_CHUNK_SIZE));

    await webrtcStore.sendRequest({
      method,
      path: appendQueryParam(path, 'uploadId', file.uploadId),
      timeoutMs: UPLOAD_REQUEST_TIMEOUT_MS,
      headers: {
        ...headers,
        'X-Upload-Init': 'true',
        'X-Upload-Field': encodeURIComponent(file.fieldName),
        'X-File-Name': encodeURIComponent(file.fileName),
        'X-File-Type': file.mimeType,
        'X-File-Size': file.size.toString(),
        'X-Total-Chunks': totalChunks.toString(),
      },
    });

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
      const start = chunkIndex * UPLOAD_CHUNK_SIZE;
      const end = Math.min(start + UPLOAD_CHUNK_SIZE, file.size);
      const chunk = file.file.slice(start, end);
      const buffer = new Uint8Array(await chunk.arrayBuffer());

      await webrtcStore.sendBinaryChunk(
        'upload',
        {
          uploadId: file.uploadId,
          chunkIndex,
          totalChunks,
          final: chunkIndex === totalChunks - 1,
        },
        buffer,
      );

      uploadedBytes += chunk.size;
      options.onProgress?.((uploadedBytes / totalBytes) * 100);
    }
  }

  const manifest = encodeURIComponent(
    JSON.stringify({
      fields,
      files: files.map((f) => omitKeys(f, ['file'])),
    }),
  );

  return webrtcStore.sendRequest({
    method,
    path: appendQueryParam(path, 'uploadBundleId', `bundle_${Date.now()}`),
    timeoutMs: UPLOAD_REQUEST_TIMEOUT_MS,
    headers: {
      ...headers,
      'X-Upload-Complete': 'true',
      'X-Upload-Manifest': manifest,
    },
  });
}

/**
 * Chunked upload helper for large files
 */
export async function uploadFileViaWebRTC(
  path: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<unknown> {
  const formData = new FormData();
  formData.append('file', file, file.name);
  const result = await uploadFormDataViaWebRTC(path, formData, {
    method: 'POST',
    onProgress,
  });

  try {
    return JSON.parse(result.body);
  } catch {
    return result;
  }
}

/**
 * Chunked download helper for large files
 */
export async function downloadFileViaWebRTC(path: string, onProgress?: (percent: number) => void): Promise<Blob> {
  const webrtcStore = useWebRTCStore();

  if (!webrtcStore.connected) {
    throw new Error('WebRTC not connected');
  }

  debugApiRouter(`[ApiRouter] Downloading file via WebRTC: ${path}`);

  const downloadId = `download_${Date.now()}_${Math.random().toString(36).substring(7)}`;

  // Initiate download
  const initResponse = await webrtcStore.sendRequest({
    method: 'GET',
    path: `${path}?downloadId=${downloadId}`,
    headers: {
      'X-Download-Init': 'true',
    },
  });

  const totalChunks = parseInt(initResponse.headers['X-Total-Chunks'] || '0');
  //   const fileSize = parseInt(initResponse.headers['X-File-Size'] || '0');
  const chunks: Uint8Array[] = [];

  // Receive binary chunks via download channel
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error('Download timeout')), 300000); // 5 min

    webrtcStore.onBinaryDownloadChunk((envelope) => {
      if (envelope.header.downloadId !== downloadId) return;

      const chunkIndex = envelope.header.chunkIndex as number;
      chunks[chunkIndex] = envelope.payload;

      if (onProgress && totalChunks > 0) {
        onProgress(((chunkIndex + 1) / totalChunks) * 100);
      }

      // Check if this is the final chunk, or all chunks received
      const complete =
        envelope.header.final === true || (totalChunks > 0 && chunks.filter((c) => c).length === totalChunks);

      if (complete) {
        clearTimeout(timeout);

        // Combine chunks
        const totalLength = chunks.reduce((acc, c) => acc + (c?.length ?? 0), 0);
        const combined = new Uint8Array(totalLength);
        let offset = 0;
        for (const chunk of chunks) {
          if (chunk) {
            combined.set(chunk, offset);
            offset += chunk.length;
          }
        }

        const blob = new Blob([combined]);
        debugApiRouter(`[ApiRouter] Download complete: ${downloadId} (${blob.size} bytes)`);
        resolve(blob);
      }
    });
  });
}
